import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import useAuth from 'hooks/useAuth';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetActiveBankAccountsQuery } from 'store/api/companies';
import { useGetContactByIdQuery, useUpdateContactMutation } from 'store/api/contacts';
import { ClassifierData } from 'types/classifiers';
import { CompanyBankAccountsGroupedDto } from 'types/companies';
import { ContactDto, ContactForm, PartyType } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { useClassifiers } from 'utils/useClassifiers';
import BlockingPolicyEditor from '../blocking/BlockingPolicyEditor';
import {
    CONTACT_FORM_CLASSIFIERS,
    ContactSectionId,
    contactDtoToForm,
    contactFormToRequest,
    contactSectionFields,
} from '../contactFormMapping';
import { mergeFieldEnv, mergeFieldRegistry, refuseTypeChangeMissingRequired } from '../merge/mergeFields';
import { AboutFields } from './AboutFields';
import { BillingFields } from './BillingFields';
import { ChooseTypeFields } from './ChooseTypeFields';
import { CompanyFields } from './CompanyFields';
import { ComplianceFields } from './ComplianceFields';
import { ContactDetailsFields } from './ContactDetailsFields';
import { IdentityDocumentFields } from './IdentityDocumentFields';
import { MarketingFields } from './MarketingFields';
import { OtherFields } from './OtherFields';
import { PersonFields } from './PersonFields';
import { ContactFormState, useContactForm } from './useContactForm';
import { useAdministratorField } from 'hooks/useAdministratorField';

const SECTION_FIELDS: Record<ContactSectionId, (form: ContactFormState) => ReactNode> = {
    'choose-type': (form) => <ChooseTypeFields form={form} />,
    about: (form) => <AboutFields form={form} />,
    person: (form) => <PersonFields form={form} />,
    company: (form) => <CompanyFields form={form} />,
    identity: (form) => <IdentityDocumentFields form={form} />,
    'contact-details': (form) => <ContactDetailsFields form={form} />,
    'billing-vat': (form) => <BillingFields form={form} part="billing-vat" />,
    'billing-address': (form) => <BillingFields form={form} part="billing-address" />,
    'billing-delivery': (form) => <BillingFields form={form} part="billing-delivery" />,
    'billing-recipients': (form) => <BillingFields form={form} part="billing-recipients" />,
    'billing-rules': (form) => <BillingFields form={form} part="billing-rules" />,
    marketing: (form) => <MarketingFields form={form} />,
    other: (form) => <OtherFields form={form} />,
    'blocking-cases': (form) => <BlockingPolicyEditor contact={form.contact} axis="CASES" />,
    'blocking-timecards': (form) => <BlockingPolicyEditor contact={form.contact} axis="TIMECARDS" />,
    'blocking-aml-timecards': (form) => <BlockingPolicyEditor contact={form.contact} axis="AML_TIMECARDS" />,
    compliance: (form) => <ComplianceFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
};

// Referentially stable while the query is unresolved, as on the edit page.
const NO_BANK_ACCOUNTS: CompanyBankAccountsGroupedDto[] = [];

interface SectionFormProps {
    section: ContactSectionId;
    contact: ContactDto;
    initialData: ContactForm;
    classifiers: Record<string, ClassifierData>;
    bankAccountsGrouped: CompanyBankAccountsGroupedDto[];
    customFields: CustomField[];
    /** The custom fields as defined, with the relationship types each is mandatory for. */
    customFieldDefinitions: CustomField[];
    onClose: () => void;
}

function ContactSectionForm({
    section,
    contact,
    initialData,
    classifiers,
    bankAccountsGrouped,
    customFields,
    customFieldDefinitions,
    onClose,
}: SectionFormProps) {
    // A type change is saved by its confirmation, not by Save, and the contact refetches after it.
    const form = useContactForm({
        type: 'edit',
        initialData,
        classifiers,
        isDirectlyConnectedToMatterAsClient: contact.isDirectlyConnectedToMatterAsClient,
        contact,
        refetch: onClose,
        entityCustomFields: customFields,
    });
    const { user } = useAuth();
    const administratorFieldName = useAdministratorField().name;
    // The page's own query argument, so the refetch refreshes the contact it shows.
    const { id: contactId = '' } = useParams();
    const { refetch } = useGetContactByIdQuery(contactId);
    const [updateContact] = useUpdateContactMutation();

    // The PUT replaces the whole contact, so every other field goes as stored now, collections in full; the VAT tick
    // that choosing Client clears goes as stored too. The blocking change reasons travel on the blocks rows, as on
    // the edit page.
    const onSave = (values: ContactForm) =>
        saveSection(values, {
            section,
            sectionFields: contactSectionFields(contact.partyType),
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto: ContactDto) =>
                contactDtoToForm(dto, {
                    classifiers,
                    bankAccountsGrouped,
                    customFieldValues: form.customFieldValuesOf(dto.customFields as CustomField[]),
                }),
            toRequest: (edited, latest, latestDto) => {
                refuseTypeChangeMissingRequired(
                    edited,
                    latest,
                    mergeFieldRegistry(customFields, customFieldDefinitions),
                    mergeFieldEnv(
                        user?.enabledFeatures,
                        user?.enabledSettings,
                        !!form.isEinvoicingEnabled,
                        user?.requiredFields,
                        administratorFieldName
                    )
                );
                return contactFormToRequest(edited, {
                    partyType: edited.partyType,
                    mapCustomFields: form.mapCustomFieldsToDto,
                    existingBlocks: latestDto.blocks ?? [],
                    isEinvoicingEnabled: form.isEinvoicingEnabled,
                    initialForm: latest,
                });
            },
            update: (request) => updateContact({ ...request, id: contact.id, entityFieldChangeReasons: null }).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoadingCustomFieldData) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose} formKey="contact-edit">
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: ContactSectionId;
    contact: ContactDto;
    onClose: () => void;
}

function ContactSectionEditor({ section, contact, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: CONTACT_FORM_CLASSIFIERS });
    const { data: bankAccountsGrouped = NO_BANK_ACCOUNTS, isLoading: isLoadingBankAccounts } =
        useGetActiveBankAccountsQuery();
    const {
        customFields,
        customFieldsEmpty,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFields,
    } = useCustomFields({
        entityType: contact.partyType === PartyType.COMPANY ? EntityType.PARTY_COMPANY : EntityType.PARTY_PERSON,
        fields: contact.customFields as CustomField[],
    });

    // It waits for the bank accounts too: the preferred one is matched against them.
    const initialData = useFrozenOnceLoaded(
        !isLoadingClassifiers && !isLoadingCustomFields && !isLoadingBankAccounts,
        () =>
            contactDtoToForm(contact, {
                classifiers,
                bankAccountsGrouped,
                customFieldValues: initialCustomFieldsObject,
            })
    );

    if (!initialData) {
        return <Loader />;
    }
    return (
        <ContactSectionForm
            section={section}
            contact={contact}
            initialData={initialData}
            classifiers={classifiers}
            bankAccountsGrouped={bankAccountsGrouped}
            customFields={customFields}
            customFieldDefinitions={customFieldsEmpty}
            onClose={onClose}
        />
    );
}

interface ContactSectionProps {
    id: ContactSectionId;
    titleId: string;
    contact: ContactDto;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** Classes of the section while it edits. */
    className?: string;
    /** This section's own gate, on top of the contact's. */
    canEdit?: boolean;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/** A contact section on the record page that edits in place with the contact form's fields for that section. */
export function ContactSection({ id, titleId, contact, children, className, canEdit, rows }: ContactSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <ContactSectionEditor section={id} contact={contact} onClose={close} />}
            className={className}
            canEdit={canEdit}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
